import { NavigationEnd, Router } from '@angular/router';
import { SidebarMenu, DialogMenu } from '../../models/sidebar-menu.model';
import { Role } from 'src/app/features/stock-summary/enums/role.enum';
import { LayoutMainComponent } from '../../pages/layout-main/layout-main.component';
import { SidebarService } from '../../services/sidebar.service';
import { AccountService } from 'src/app/features/stock-summary/services/account.service';
import { filter, Subject, takeUntil, switchMap, withLatestFrom, map } from 'rxjs';
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DatecodeInquiryComponent } from 'src/app/features/stock-summary/pages/datecode-inquiry/datecode-inquiry.component';
import { AlternativePartInquiryComponent } from 'src/app/features/stock-summary/pages/alternative-part-inquiry/alternative-part-inquiry.component';
import { SaveDownloadFileService } from "src/app/core/services/save-download-file.service";
import { ItemShareStockComponent } from 'src/app/features/stock-summary/pages/item-share-stock/item-share-stock.component';
import { SalesCostInquiryComponent } from 'src/app/features/stock-summary/pages/sales-cost-inquiry/sales-cost-inquiry.component';
import { UcInqByPartNoComponent } from 'src/app/features/stock-summary/pages/uc-inq-by-part-no/uc-inq-by-part-no.component';
import { WHInvBookkeepingSearchComponent } from 'src/app/features/stock-summary/pages/w-h-inv-bookkeeping-search/w-h-inv-bookkeeping-search.component';
import { MajorCustomerAnalysisComponent } from 'src/app/features/stock-summary/pages/major-customer-analysis/major-customer-analysis.component';
import { InterCorpIrIsoComponent } from 'src/app/features/stock-summary/pages/inter-corp-ir-iso/inter-corp-ir-iso.component';
import { InternalTransferPriceInquiryComponent } from 'src/app/features/stock-summary/pages/internal-transfer-price-inquiry/internal-transfer-price-inquiry.component';

@Component({
  selector: 'app-layout-sidebar',
  templateUrl: './layout-sidebar.component.html',
  styleUrls: ['./layout-sidebar.component.scss']
})
export class LayoutSidebarComponent implements OnInit, OnDestroy {

  @Input()
  sidebarVisible: boolean = false;

  private readonly _destroying$ = new Subject<void>();

  menuData?: SidebarMenu[];

  filepath = "admin-guide.file-path";
  filename = "admin-guide.file-name";

  /**
   * 記錄目前 router 的網址，不含網址列的參數
   * 作為 routerLinkActiveOptions exact 的判斷依據
   * exact = true 表示只有當 routerLink 的值完全符合時，才會套用 active class
   * 例如 routerLink = '/layout/main'，當 router 的網址是 '/layout/main?type=1' 時，不會套用 active class
   * 但如果設定 exact = false，當 router 的網址是 '/layout/main' 時，'/layout/main' 和 '/layout/main/page1' 都會套用 active class
   * 所以不能單純設定 false，才會用這個 currentUrl 來判斷
   */
  currentUrl: string = '';

  constructor(public sidebarService: SidebarService,
    public layoutMain: LayoutMainComponent,
    private readonly translateService: TranslateService,
    private readonly dialogService: DialogService,
    private readonly accountService: AccountService,
    private readonly saveDownloadFileService: SaveDownloadFileService,
    private readonly router: Router) { }

  ngOnInit(): void {
    console.log('LayoutSidebarComponent ngOnInit');
    const inventoryManagementMenuItems = [
      {
        path: 'menu.dialog.item-lock-share-stock',
        role: [Role.SALES, Role.PM, Role.PCSR, Role.CSR, Role.FAE],
        command: () =>
          this.openFunctionDialog(
            ItemShareStockComponent,
            this.translateService.instant('label.item-lock-share-stock'),
            '1020px',
            'auto',
            null,
            { 'overflow-y': 'auto' }
          )
      },
      {
        path: 'menu.dialog.uc-inq-by-part-no',
        role: [Role.SALES, Role.PM, Role.PCSR, Role.CSR, Role.FAE],
        command: () =>
          this.openFunctionDialog(
            UcInqByPartNoComponent,
            this.translateService.instant('label.uc-inq-by-part-no'),
            '1200px',
            'auto',
            null,
            { 'overflow-y': 'auto' }
          )
      },
      {
        path: 'menu.dialog.wh-inv-bookkeeping',
        role: [Role.SALES, Role.PM, Role.PCSR, Role.CSR, Role.FAE],
        command: () =>
          this.openFunctionDialog(
            WHInvBookkeepingSearchComponent,
            this.translateService.instant('label.wh-inv-bookkeeping'),
            '1020px',
            'auto',
            null,
            { 'overflow-y': 'auto' }
          )
      },
      {
        path: 'menu.dialog.major-customer-analysis',
        role: [Role.SALES, Role.PM, Role.PCSR, Role.CSR, Role.FAE],
        command: () =>
          this.openFunctionDialog(
            MajorCustomerAnalysisComponent,
            this.translateService.instant('label.major-customer-analysis'),
            '1020px',
            'auto',
            null,
            { 'overflow-y': 'auto' }
          )
      },
      {
        path: 'menu.dialog.inter-corp-iriso',
        role: [Role.SALES, Role.PM, Role.PCSR, Role.CSR, Role.FAE],
        command: () =>
          this.openFunctionDialog(
            InterCorpIrIsoComponent,
            this.translateService.instant('label.inter-corp-iriso'),
            '80vw',
            'auto',
            null,
            { 'overflow-y': 'auto' }
          )
      }
    ];
    const salesAndCostAnalysisMenuItems = [
      {
        path: 'menu.dialog.sales-cost-inquiry',
        role: [Role.SALES, Role.PM, Role.PCSR, Role.CSR, Role.FAE],
        command: () =>
          this.openFunctionDialog(
            SalesCostInquiryComponent,
            this.translateService.instant('label.sales-cost-inquiry'),
            '1020px',
            'auto',
            null,
            { 'overflow-y': 'auto' }
          )
      },
      {
        path: 'menu.dialog.internal-transfer-price-inquiry',
        role: [Role.SALES, Role.PM, Role.PCSR, Role.CSR, Role.FAE],
        command: () =>
          this.openFunctionDialog(
            InternalTransferPriceInquiryComponent,
            this.translateService.instant('label.internal-transfer-price-inquiry'),
            '1020px',
            'auto',
            null,
            { 'overflow-y': 'auto' }
          )
      }
    ];
    const otherFunctionsMenuItems = [
      {
        path: 'menu.dialog.dateCode-inquiry',
        role: [Role.SALES, Role.PM, Role.PCSR, Role.CSR, Role.FAE],
        command: () => 
          this.openFunctionDialog(
            DatecodeInquiryComponent,
            this.translateService.instant('label.dateCode-inquiry'),
            '1020px',
            'auto',
            null,
            { 'overflow-y': 'auto' }
          )
      },
      {
        path: 'menu.dialog.alternative-part-inquiry',
        role: [Role.SALES, Role.PM, Role.PCSR, Role.CSR],
        command: () =>       
          this.openFunctionDialog(
            AlternativePartInquiryComponent,
            this.translateService.instant('label.alternative-part-inquiry'),
            '1020px',
            'auto',
            null,
            { 'overflow-y': 'auto' }
        )
      },
    ]

    // 記錄目前 router 的網址，不含網址列的參數
    this.updateCurrentUrl();

    // 訂閱路由變更事件
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this._destroying$)
    ).subscribe(() => {
      this.updateCurrentUrl();
    });
    // 合併menu
    const dialogMenuItems = [
      ...otherFunctionsMenuItems,
      ...inventoryManagementMenuItems,
      ...salesAndCostAnalysisMenuItems
    ];

    this.accountService.setCurrentRole().pipe(
      filter((role): role is Role[] => !!role), // 確保角色不為 undefined
      switchMap(role =>
        // 取得使用者角色後，取得選單資料
        this.sidebarService.getMenus().pipe(
          map(menus => ({
            menus: menus.map(menu => ({
              ...menu,
              subMenus: menu.subMenus
                ? this.mapSubMenusWithCommands(menu.subMenus, role, dialogMenuItems)
                : undefined
            })),
            role
          }))
        )
      ),
      takeUntil(this._destroying$)
    ).subscribe(({ menus }) => {
      this.clearIcon(menus);
      this.menuData = menus;
      this.setSelected(this.menuData);
    });

  }

  mapSubMenusWithCommands(
    subMenus: SidebarMenu[],
    roles: Role[],
    otherFunctionsMenuItems: DialogMenu[]
  ): SidebarMenu[] {
    const isAdmin = roles.includes(Role.ADMIN);

    // 預先建立 path 對應的 Map，加快查找效率
    const menuMap = new Map<string, DialogMenu>(
      otherFunctionsMenuItems.map(item => [item.path, item])
    );

    return subMenus
      .filter(sub => {
        const matched = menuMap.get(sub.path);
        if (!matched) return false;

        return isAdmin || matched.role.some(r => roles.includes(r));
      })
      .map(sub => {
        const matched = menuMap.get(sub.path);
        return {
          ...sub,
          command: matched?.command
        };
      });
  }

  clearIcon(sidebarMenus: SidebarMenu[]) {
    sidebarMenus.forEach(sidebarMenu => {
      sidebarMenu.icon = '';
      sidebarMenu.selected = false;
      if (sidebarMenu.subMenus) {
        this.clearIcon(sidebarMenu.subMenus);
      }
    });
  }

  /**
   * 設定選單展開的狀態，避免 back 回上一頁或直接開啟某網址，左側選單是縮起來的
   *
   * @param sidebarMenus
   * @returns 是否有選單符合目前 route 的網址
   */
  setSelected(sidebarMenus: SidebarMenu[]): boolean {
    const currentPath = this.router.url;
    let pathMatch = false;
    sidebarMenus.forEach(sidebarMenu => {
      if (sidebarMenu.path && currentPath.startsWith(sidebarMenu.path)) {
        pathMatch = true;
      } else {
        if (sidebarMenu.subMenus) {
          pathMatch = this.setSelected(sidebarMenu.subMenus);
        } else {
          pathMatch = false;
        }
      }

      if (pathMatch) {
        sidebarMenu.selected = true;
      }
    });
    return pathMatch || sidebarMenus.some(menu => menu.selected === true);
  }

  // 切換 sidebar step 5 : 用 subject 的 next 方法傳送值，所有訂閱的 observer 就會接收到
  toggleSidebar() {
    console.log('LayoutSidebarComponent: call by app-layout-topbar sidebarToggled Event Binding');
    this.sidebarService.toggleSidebar$.next('toggle');
  }

  hideSidebar() {
    console.log('LayoutSidebarComponent: call by self onHide Event');
    this.sidebarService.toggleSidebar$.next('hide');
  }

  private updateCurrentUrl(): void {
    this.currentUrl = this.router.url.split('?')[0];
  }

  ngOnDestroy() {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  /**
   * 透過 Dialog 開啟 Detail Menu 上各種功能
   * @param component 要開啟的功能
   * @param header 標題名稱 i18n
   */
  private openFunctionDialog(
    component: any,
    header: string,
    width?: string,
    height?: string,
    data?: any,
    contentStyle?: {
      [klass: string]: any;
    }
  ): void {
    const isMobile = window.innerWidth <= 768;
    this.dialogService.open(component, {
      modal: false,
      draggable: true,
      resizable: true,
      header: this.translateService.instant(header),
      width: isMobile ? '98vw' : width ?? 'auto',
      height: isMobile ? '99vh' : height ?? 'auto',
      data: data ?? {},
      duplicate: true,
      contentStyle: contentStyle,
    });
  }

  /** 下載文件 */
  downloadUserGuide(): void {
    const filepath = this.translateService.instant(this.filepath);
    const filename = this.translateService.instant(this.filename);
    this.sidebarService.downloadUserGuide(filepath, filename).subscribe({
      next: (arrayBuffer) => {
        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
          return;
        }
        this.saveDownloadFileService.saveBufferAsPptx(
          arrayBuffer.body,
          arrayBuffer.fileName
        );
      },
    });
  }
}
